import React, { useState, useEffect, useRef } from 'react';
import { useApi } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { CheckCircle2, AlertTriangle, XCircle, Bot, Bell, BellOff, Sparkles, Zap } from 'lucide-react';

const C = { navy: '#0D1B2A', blue: '#1A4C7C', teal: '#00B4D8', green: '#1D9E75', red: '#E24B4A', amber: '#E8A020', slate: '#5C6B7A', saffron: '#FF9933' };
const OUTCOME = {
  auto_issued: { label: 'Auto-Issued', color: C.green, icon: CheckCircle2 },
  issued_after_review: { label: 'Issued', color: C.green, icon: CheckCircle2 },
  escalated: { label: 'Escalated', color: C.amber, icon: AlertTriangle },
  declined: { label: 'Declined', color: C.red, icon: XCircle },
  declined_after_review: { label: 'Declined', color: C.red, icon: XCircle },
};
const HIGH_VALUE = 50000;
const fmt$ = (v) => v >= 1e6 ? `$${(v / 1e6).toFixed(1)}M` : v >= 1e3 ? `$${(v / 1e3).toFixed(0)}K` : `$${Number(v || 0).toLocaleString()}`;
const ago = (iso) => {
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${Math.floor(s)}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

export function AutonomyFeed() {
  const api = useApi();
  const [digest, setDigest] = useState(null);
  const [runs, setRuns] = useState([]);
  const [pulse, setPulse] = useState(false);
  const [flash, setFlash] = useState(null);
  const [soundOn, setSoundOn] = useState(false);
  const [expandedRun, setExpandedRun] = useState(null);
  const [resolving, setResolving] = useState(false);
  const firstRun = useRef(null);
  const audioRef = useRef(null);
  const soundRef = useRef(false);
  useEffect(() => { soundRef.current = soundOn; }, [soundOn]);

  const beep = () => {
    if (!soundRef.current) return;
    try {
      if (!audioRef.current) audioRef.current = new (window.AudioContext || window.webkitAudioContext)();
      const ctx = audioRef.current;
      const o = ctx.createOscillator(); const g = ctx.createGain();
      o.connect(g); g.connect(ctx.destination);
      o.type = 'sine'; o.frequency.value = 880;
      g.gain.setValueAtTime(0.0001, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.12, ctx.currentTime + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.25);
      o.start(); o.stop(ctx.currentTime + 0.26);
    } catch (e) { /* autoplay blocked */ }
  };

  const fetchFeed = (detectNew = true) => {
    return Promise.all([api.get('/pipeline/digest'), api.get('/pipeline/runs?limit=8')])
      .then(([d, r]) => {
        setDigest(d.data); setRuns(r.data);
        const top = r.data[0];
        if (detectNew && firstRun.current && top && top.run_id !== firstRun.current) {
          setPulse(true); setTimeout(() => setPulse(false), 1200);
          const hv = (top.premium || 0) >= HIGH_VALUE && ['auto_issued', 'escalated'].includes(top.outcome);
          if (hv) { setFlash(top); beep(); setTimeout(() => setFlash(null), 6500); }
        }
        firstRun.current = top?.run_id;
      }).catch(() => {});
  };

  useEffect(() => {
    let alive = true;
    fetchFeed(false);
    const id = setInterval(() => { if (alive) fetchFeed(true); }, 5000);
    return () => { alive = false; clearInterval(id); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);

  const resolve = (runId, action) => {
    setResolving(true);
    api.post(`/pipeline/runs/${runId}/resolve`, { action })
      .then(() => { toast.success(action === 'issue' ? 'Issued by underwriter' : 'Declined by underwriter'); setExpandedRun(null); return fetchFeed(false); })
      .catch(e => toast.error(e?.response?.data?.detail || 'Failed'))
      .finally(() => setResolving(false));
  };

  if (!digest) return null;
  const t = digest.today;
  const maxBar = Math.max(...digest.series.map(s => s.count), 1);

  return (
    <Card className="rounded-md overflow-hidden" style={{ border: '1px solid rgba(136,150,168,0.16)' }} data-testid="autonomy-feed">
      <div className="px-4 py-3 flex items-center justify-between border-b" style={{ borderColor: 'rgba(136,150,168,0.1)', background: '#0B1E3D' }}>
        <div className="flex items-center gap-2">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ background: C.green }} />
            <span className="relative inline-flex rounded-full h-2 w-2" style={{ background: C.green }} />
          </span>
          <Bot className="w-3.5 h-3.5" style={{ color: C.saffron }} />
          <h3 className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'white' }}>Mission Control — Live</h3>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => setSoundOn(v => !v)} data-testid="autonomy-sound-toggle" title={soundOn ? 'Mute high-value alerts' : 'Enable high-value alert sound'}
            className="flex items-center gap-1 text-[10px] uppercase tracking-wider" style={{ color: soundOn ? C.saffron : 'rgba(255,255,255,0.45)' }}>
            {soundOn ? <Bell className="w-3.5 h-3.5" /> : <BellOff className="w-3.5 h-3.5" />}{soundOn ? 'Alerts on' : 'Muted'}
          </button>
          <span className="text-[10px] uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.4)' }}>5s</span>
        </div>
      </div>

      {/* High-value ping banner */}
      {flash && (
        <div className="px-4 py-2.5 flex items-center gap-2 border-b animate-pulse" style={{ background: flash.outcome === 'escalated' ? '#FFF6E8' : '#EAFBF3', borderColor: 'rgba(136,150,168,0.1)' }} data-testid="autonomy-flash">
          {flash.outcome === 'escalated' ? <Zap className="w-4 h-4" style={{ color: C.amber }} /> : <Sparkles className="w-4 h-4" style={{ color: C.green }} />}
          <span className="text-xs font-semibold" style={{ color: C.navy }}>
            High-value {flash.outcome === 'escalated' ? 'escalation' : 'auto-issue'}: {flash.business_name} — {fmt$(flash.premium)}
          </span>
        </div>
      )}

      {/* Today's digest */}
      <div className="px-4 py-3 grid grid-cols-2 md:grid-cols-5 gap-3 border-b" style={{ borderColor: 'rgba(136,150,168,0.1)', background: '#FAFBFC' }} data-testid="autonomy-digest">
        <Metric label="Today" value={t.count} sub="processed" />
        <Metric label="Auto-Issued" value={t.auto_issued} accent={C.green} />
        <Metric label="STP Rate" value={`${t.stp_rate}%`} accent={C.teal} />
        <Metric label="Auto GWP" value={fmt$(t.gwp)} accent={C.blue} />
        <Metric label="Escalated" value={t.escalated} accent={C.amber} />
      </div>

      {/* 7-day mini bars */}
      <div className="px-4 py-3 border-b" style={{ borderColor: 'rgba(136,150,168,0.1)' }}>
        <p className="text-[10px] uppercase tracking-wider mb-2" style={{ color: C.slate }}>Last 7 days</p>
        <div className="flex items-end gap-1.5 h-12">
          {digest.series.map((s, i) => (
            <div key={i} className="flex-1 flex flex-col items-center justify-end gap-1">
              <div className="w-full rounded-t" style={{ height: `${(s.count / maxBar) * 100}%`, minHeight: s.count ? 3 : 0, background: i === digest.series.length - 1 ? C.teal : '#C7D6E5' }} title={`${s.count} runs`} />
              <span className="text-[9px]" style={{ color: C.slate }}>{s.date.slice(8)}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Live stream — escalated rows are actionable */}
      <div className="max-h-80 overflow-y-auto" data-testid="autonomy-stream">
        {runs.length === 0 && <p className="p-4 text-xs" style={{ color: C.slate }}>No runs yet. Activity will stream here as the pipeline processes submissions.</p>}
        {runs.map((r, idx) => {
          const o = OUTCOME[r.outcome] || { label: r.outcome, color: C.slate, icon: Bot };
          const OIcon = o.icon;
          const actionable = r.needs_review;
          const open = expandedRun === r.run_id;
          const hv = (r.premium || 0) >= HIGH_VALUE;
          return (
            <div key={r.run_id} className="border-b" style={{ borderColor: 'rgba(136,150,168,0.06)' }}>
              <div onClick={() => actionable && setExpandedRun(open ? null : r.run_id)}
                data-testid={`autonomy-run-${r.run_id}`}
                className={`flex items-center justify-between gap-2 px-4 py-2.5 transition-colors ${actionable ? 'cursor-pointer hover:bg-[#FFF9F0]' : ''}`}
                style={{ background: idx === 0 && pulse ? `${C.green}10` : 'transparent' }}>
                <div className="min-w-0 flex items-center gap-2">
                  {hv && <span title="High-value" className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: C.saffron }} />}
                  <div className="min-w-0">
                    <span className="text-xs font-medium" style={{ color: C.navy }}>{r.business_name}</span>
                    <p className="text-[10px]" style={{ color: C.slate }}>AIRA {r.composite_score} · {fmt$(r.premium)} · {r.total_ms}ms · {ago(r.created_at)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <span className="flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded" style={{ background: o.color + '18', color: o.color }}>
                    <OIcon className="w-3 h-3" />{o.label}
                  </span>
                  {actionable && <span className="text-[9px] uppercase tracking-wider" style={{ color: C.amber }}>{open ? 'Close' : 'Review'}</span>}
                </div>
              </div>
              {open && actionable && (
                <div className="px-4 pb-3 flex items-center gap-2" data-testid={`autonomy-resolve-${r.run_id}`}>
                  <span className="text-[11px]" style={{ color: C.slate }}>Underwriter decision:</span>
                  <button disabled={resolving} onClick={(e) => { e.stopPropagation(); resolve(r.run_id, 'issue'); }}
                    data-testid={`autonomy-issue-${r.run_id}`}
                    className="text-[10px] font-semibold px-2.5 py-1.5 rounded" style={{ background: C.green, color: 'white' }}>Issue policy</button>
                  <button disabled={resolving} onClick={(e) => { e.stopPropagation(); resolve(r.run_id, 'decline'); }}
                    data-testid={`autonomy-decline-${r.run_id}`}
                    className="text-[10px] font-semibold px-2.5 py-1.5 rounded" style={{ background: 'white', color: C.red, border: `1px solid ${C.red}40` }}>Decline</button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function Metric({ label, value, sub, accent }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider" style={{ color: C.slate }}>{label}</p>
      <p className="text-lg font-bold font-mono leading-tight" style={{ color: accent || C.navy }}>{value}</p>
      {sub && <p className="text-[9px]" style={{ color: C.slate }}>{sub}</p>}
    </div>
  );
}
